import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  AutoAwesome,
  TrendingUp,
  Save,
  Publish,
  Schedule
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import toast from 'react-hot-toast';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchTrendingTopics,
  startGeneratePost,
  regeneratePostPart,
  shortenForX,
  getImageSuggestions,
  searchImagesBatch,
  selectImageFromSearch,
  setImageQuery,
  clearImageCandidates,
  setGeneratedContent,
  applyPostVariant,
  setActivePlatform,
  updatePlatformDraftContent,
  clearGeneratedContent,
  applyGenerateResult,
  updateGenerationJob,
  failGenerationJob,
  clearGenerationJob,
  clearError,
} from '../store/slices/contentSlice';
import postsAPI from '../services/postsAPI';
import brandAPI, { BrandGuidelineData } from '../services/brandAPI';
import integrationsAPI from '../services/integrationsAPI';
import socialAPI, { SocialPostResult } from '../services/socialAPI';
import { getImageUrl } from '../utils/api';
import PlatformContentTabs from '../components/Posts/PlatformContentTabs';
import PostVariantPicker from '../components/Posts/PostVariantPicker';
import ImageSearchPanel from '../components/Posts/ImageSearchPanel';
import MultiPublishPanel from '../components/Posts/MultiPublishPanel';
import PublishResultsDialog from '../components/Posts/PublishResultsDialog';
import GenerationJobBanner from '../components/Posts/GenerationJobBanner';
import contentAPI from '../services/contentAPI';
import { GENERATE_PLATFORMS } from '../constants/platforms';
import { platformDraftsToPayload } from '../utils/platformContent';
import {
  buildCustomContent,
  getOverLimitSelections,
  getPublishableDraftKeys,
  INTEGRATION_TO_DRAFT,
  DRAFT_TO_API_PLATFORM,
} from '../utils/publishPlatforms';
import { draftHasRefusal } from '../utils/contentQuality';
import { ImageCandidate } from '../types/api';

export const CreatePostPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { trendingTopics, generatedContent, postVariants, selectedVariantIndex, platformDrafts, activePlatform, selectedImage, imageSuggestions, imageQuery, imageCandidates, imageSuggestionsLoading, imageSearchLoading, imageSelecting, loading, queueingPost, generationJob, partRegenerating, shorteningForX, error } = useAppSelector(
    state => state.content
  );
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  
  const [topic, setTopic] = useState('');
  const [selectedTopic, setSelectedTopic] = useState<any>(null);
  const [imageOrientation, setImageOrientation] = useState<'landscape' | 'portrait' | 'squarish'>('landscape');
  
  // Button action states
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduledTime, setScheduledTime] = useState<Dayjs | null>(dayjs().add(1, 'hour'));
  const [actionLoading, setActionLoading] = useState(false);
  const [savedDraftId, setSavedDraftId] = useState<string | null>(null);
  const [selectedPreviewUrl, setSelectedPreviewUrl] = useState<string | undefined>();
  const [activeBrand, setActiveBrand] = useState<BrandGuidelineData | null>(null);
  const [generateThreeVariants, setGenerateThreeVariants] = useState(false);
  const [connectedDraftKeys, setConnectedDraftKeys] = useState<Set<string>>(new Set());
  const [selectedPublishPlatforms, setSelectedPublishPlatforms] = useState<string[]>([]);
  const [publishResults, setPublishResults] = useState<SocialPostResult[]>([]);
  const [publishResultsOpen, setPublishResultsOpen] = useState(false);
  const activePollTaskRef = useRef<string | null>(null);

  const isGenerating = Boolean(
    generationJob && generationJob.status !== 'SUCCESS' && generationJob.status !== 'FAILURE'
  );

  const pollGenerationTask = useCallback(
    async (taskId: string) => {
      if (activePollTaskRef.current !== taskId) return;

      try {
        const task = await contentAPI.getGeneratePostTaskStatus(taskId);
        if (activePollTaskRef.current !== taskId) return;

        const message =
          task.status === 'STARTED'
            ? 'Generating your post with AI...'
            : task.status === 'PENDING'
              ? "Queued — we're creating your post in the background..."
              : generationJob?.message ?? '';

        dispatch(updateGenerationJob({ status: task.status, message }));

        if (task.successful && task.result) {
          dispatch(applyGenerateResult(task.result));
          activePollTaskRef.current = null;
          if (task.result.variants?.length) {
            toast.success('Generated 3 tone variants — pick your favorite');
          } else {
            toast.success('Content generated for LinkedIn, X & Instagram');
          }
          window.setTimeout(() => dispatch(clearGenerationJob()), 4000);
          return;
        }

        if (task.failed) {
          const err = task.error || 'Generation failed';
          dispatch(failGenerationJob(err));
          activePollTaskRef.current = null;
          toast.error(err);
        }
      } catch (pollError: unknown) {
        if (activePollTaskRef.current !== taskId) return;
        const msg =
          pollError instanceof Error ? pollError.message : 'Failed to check generation status';
        dispatch(failGenerationJob(msg));
        activePollTaskRef.current = null;
        toast.error(msg);
      }
    },
    [dispatch, generationJob?.message]
  );

  useEffect(() => {
    if (!generationJob?.taskId) return undefined;
    if (generationJob.status === 'SUCCESS' || generationJob.status === 'FAILURE') {
      return undefined;
    }

    activePollTaskRef.current = generationJob.taskId;
    pollGenerationTask(generationJob.taskId);

    const intervalId = window.setInterval(() => {
      pollGenerationTask(generationJob.taskId);
    }, 2500);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [generationJob?.taskId, generationJob?.status, pollGenerationTask]);

  const activePostContent = platformDrafts.linkedin?.content || generatedContent?.content || '';

  useEffect(() => {
    if (!isAuthenticated) {
      setActiveBrand(null);
      return;
    }
    brandAPI.getGuidelines()
      .then((res) => {
        if (res.has_guidelines && res.guideline) {
          setActiveBrand(res.guideline);
        }
      })
      .catch(() => {
        // Brand guidelines are optional — ignore fetch errors
      });
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      setConnectedDraftKeys(new Set());
      return;
    }
    integrationsAPI.getConnectedAccounts()
      .then((res) => {
        const connected = new Set<string>();
        for (const account of res.accounts || []) {
          if (account.connected && !account.needs_reauth) {
            const draftKey = INTEGRATION_TO_DRAFT[account.platform];
            if (draftKey) connected.add(draftKey);
          }
        }
        setConnectedDraftKeys(connected);
      })
      .catch(() => {
        // Connection status is optional for draft editing
      });
  }, [isAuthenticated]);

  const openPublishDialog = () => {
    const linkedinContent = getCurrentContent('linkedin');
    if (!linkedinContent.trim() && Object.keys(platformDrafts).length === 0) {
      toast.error('Please add some content before publishing');
      return;
    }

    const defaults = getPublishableDraftKeys(platformDrafts).filter((key) =>
      connectedDraftKeys.has(key)
    );
    setSelectedPublishPlatforms(defaults.length > 0 ? defaults : []);
    setPublishConfirmOpen(true);
  };

  // Handle navigation state from TrendingPage
  useEffect(() => {
    const state = location.state as any;
    if (state) {
      if (state.topic) {
        setTopic(state.topic);
      }
      if (state.generatedContent) {
        dispatch(setGeneratedContent(state.fullGeneratedPost || {
          content: state.generatedContent,
          image_search_terms: state.imageSearchTerms || '',
          image: undefined,
        }));
      }
      if (state.topicDetails) {
        setSelectedTopic(state.topicDetails[0]);
      }
      
      // Show success message
      toast.success('Post generated and ready for editing!');
    }
  }, [location.state, dispatch]);

  useEffect(() => {
    if (generatedContent?.image_search_terms && !imageQuery) {
      dispatch(setImageQuery(generatedContent.image_search_terms));
    }
  }, [generatedContent?.image_search_terms, imageQuery, dispatch]);

  const handleFetchTrends = async () => {
    await dispatch(fetchTrendingTopics({
      max_results: 5,
      time_frame: 'today'
    }));
  };

  const handleGenerateContent = async () => {
    if (!topic.trim()) return;

    const result = await dispatch(startGeneratePost({
      topic: topic,
      topic_details: selectedTopic ? [selectedTopic] : undefined,
      use_topic_as_reference_only: true,
      platforms: [...GENERATE_PLATFORMS],
      variants: generateThreeVariants ? 3 : 1,
    }));

    if (startGeneratePost.fulfilled.match(result)) {
      toast.success('Request received — creating your post in the background');
    } else if (startGeneratePost.rejected.match(result)) {
      toast.error((result.payload as string) || 'Failed to queue generation');
    }
  };

  const handleSelectVariant = (index: number) => {
    dispatch(applyPostVariant(index));
    toast.success(`Applied ${postVariants[index]?.label ?? 'variant'}`);
  };

  const handleSuggestImageTitles = async () => {
    if (!topic.trim() || !activePostContent.trim()) {
      toast.error('Generate content first');
      return;
    }
    const result = await dispatch(getImageSuggestions({
      topic,
      content: activePostContent,
      count: 6,
    }));
    if (getImageSuggestions.fulfilled.match(result)) {
      if (result.payload.length === 0) {
        toast.error('No suggestions generated — try editing the search title manually');
      } else {
        toast.success(`Generated ${result.payload.length} search titles`);
      }
    }
  };

  const handleSearchImages = async () => {
    if (!imageQuery.trim()) {
      toast.error('Enter or select an image search title');
      return;
    }
    dispatch(clearImageCandidates());
    const result = await dispatch(searchImagesBatch({
      query: imageQuery.trim(),
      orientation: imageOrientation,
      per_page: 8,
    }));
    if (searchImagesBatch.fulfilled.match(result)) {
      if (result.payload.data.length === 0) {
        toast.error(result.payload.message || 'No images found — try a shorter phrase');
      }
    }
  };

  const handleSelectSuggestion = (query: string) => {
    dispatch(setImageQuery(query));
    dispatch(clearImageCandidates());
  };

  const handleSelectImage = async (candidate: ImageCandidate) => {
    setSelectedPreviewUrl(candidate.preview_url);
    const result = await dispatch(selectImageFromSearch({
      preview_url: candidate.preview_url,
      page_url: candidate.page_url,
      credit: candidate.credit,
      query: candidate.query,
    }));
    if (selectImageFromSearch.fulfilled.match(result)) {
      toast.success('Image selected');
    }
  };

  const handleRegeneratePart = async (part: 'hook' | 'hashtags') => {
    const current = platformDrafts[activePlatform]?.content;
    if (!current?.trim() || !topic.trim()) {
      toast.error('Generate content first');
      return;
    }
    const result = await dispatch(regeneratePostPart({
      topic,
      platform: activePlatform,
      current_content: current,
      part,
    }));
    if (regeneratePostPart.fulfilled.match(result)) {
      toast.success(part === 'hook' ? 'Hook updated' : 'Hashtags updated');
    }
  };

  const handleShortenForX = async () => {
    const linkedinContent = platformDrafts.linkedin?.content ?? generatedContent?.content;
    if (!linkedinContent?.trim() || !topic.trim()) {
      toast.error('Generate LinkedIn content first');
      return;
    }
    const result = await dispatch(shortenForX({
      topic,
      source_content: linkedinContent,
    }));
    if (shortenForX.fulfilled.match(result)) {
      const draft = result.payload;
      if (draft.within_limit) {
        toast.success(`X draft ready (${draft.char_count}/${draft.char_limit} chars)`);
      } else {
        toast.error('Still over limit — edit manually');
      }
    }
  };

  const handleSelectTopic = (trendingTopic: any) => {
    setSelectedTopic(trendingTopic);
    setTopic(trendingTopic.title);
  };

  const getCurrentContent = (platform?: string) => {
    const key = platform ?? activePlatform ?? 'linkedin';
    return platformDrafts[key]?.content ?? generatedContent?.content ?? '';
  };

  const hasOverLimitDraft = Object.values(platformDrafts).some((draft) => !draft.within_limit);

  const resetForm = () => {
    setTopic('');
    setSelectedTopic(null);
    setSavedDraftId(null);
    dispatch(clearGeneratedContent());
  };

  const buildDraftPayload = () => {
    const platforms = Object.keys(platformDrafts);
    const linkedinContent = platformDrafts.linkedin?.content ?? generatedContent?.content ?? '';

    return {
      topic: topic || 'Untitled Post',
      content: linkedinContent,
      platforms: platforms.length > 0 ? platforms : [...GENERATE_PLATFORMS],
      platform_content: platformDraftsToPayload(platformDrafts),
      status: 'DRAFT' as const,
      image_url: selectedImage?.url,
      image_path: selectedImage?.path,
      image_credit: selectedImage?.credit,
      image_search_terms: imageQuery || generatedContent?.image_search_terms,
    };
  };

  // Save Draft functionality
  const handleSaveDraft = async () => {
    const linkedinContent = getCurrentContent('linkedin');
    if (!linkedinContent.trim() && Object.keys(platformDrafts).length === 0) {
      toast.error('Please add some content before saving');
      return;
    }

    setActionLoading(true);
    try {
      const payload = buildDraftPayload();

      if (savedDraftId) {
        await postsAPI.updatePost(savedDraftId, payload);
        toast.success('Draft updated successfully!');
      } else {
        const response = await postsAPI.createPost(payload);
        if (response.data?.id) {
          setSavedDraftId(response.data.id);
        }
        toast.success('Draft saved successfully!');
      }
    } catch (error: any) {
      console.error('Save draft error:', error);
      toast.error('Failed to save draft. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  // Multi-platform publish
  const handlePublishNow = async () => {
    if (selectedPublishPlatforms.length === 0) {
      toast.error('Select at least one platform to publish');
      return;
    }

    const overLimit = getOverLimitSelections(platformDrafts, selectedPublishPlatforms);
    if (overLimit.length > 0) {
      toast.error('Fix character limits before publishing to X or Instagram');
      return;
    }

    setActionLoading(true);
    try {
      const payload = buildDraftPayload();
      let postId = savedDraftId;

      if (postId) {
        await postsAPI.updatePost(postId, payload);
      } else {
        const createResponse = await postsAPI.createPost(payload);
        postId = createResponse.data?.id;
        if (postId) setSavedDraftId(postId);
      }

      if (!postId) {
        throw new Error('Failed to save post before publishing');
      }

      const apiPlatforms = selectedPublishPlatforms.map(
        (key) => DRAFT_TO_API_PLATFORM[key] ?? key
      );
      const response = await socialAPI.postToMultiplePlatforms({
        post_id: postId,
        platforms: apiPlatforms,
        platform_content: platformDraftsToPayload(platformDrafts),
        custom_content: buildCustomContent(platformDrafts, selectedPublishPlatforms),
      });

      const results = response.data?.results ?? [];
      setPublishResults(results);
      setPublishConfirmOpen(false);
      setPublishResultsOpen(true);

      const allOk = results.every((r) => r.status === 'published');
      const anyOk = results.some((r) => r.status === 'published');

      if (allOk) {
        toast.success('Published to all selected platforms!');
        resetForm();
      } else if (anyOk) {
        toast.error('Some platforms failed — see details');
      } else {
        toast.error('Publish failed for all selected platforms');
      }
    } catch (error: unknown) {
      const apiErr = error as { response?: { data?: { detail?: string } }; message?: string };
      console.error('Publish error:', error);
      toast.error(
        apiErr.response?.data?.detail || apiErr.message || 'Failed to publish post'
      );
    } finally {
      setActionLoading(false);
    }
  };

  // Schedule Post functionality - Fixed with proper 2-step process
  const handleSchedulePost = async () => {
    const content = getCurrentContent('linkedin');
    if (!content.trim()) {
      toast.error('Please add some content before scheduling');
      return;
    }

    if (!scheduledTime || scheduledTime.isBefore(dayjs())) {
      toast.error('Please select a future date and time');
      return;
    }

    setActionLoading(true);
    try {
      // Use the enhanced createAndSchedulePost method
      const response = await postsAPI.createAndSchedulePost(
        {
          topic: topic || 'Untitled Post',
          content: content,
          platforms: ['linkedin'],
          image_url: selectedImage?.url,
          image_path: selectedImage?.path,
          image_credit: selectedImage?.credit
        },
        {
          scheduled_time: scheduledTime.toISOString(),
          platforms: ['linkedin'],
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        }
      );
      
      toast.success(`Post scheduled for ${scheduledTime.format('MMM DD, YYYY [at] h:mm A')}!`);
      setScheduleDialogOpen(false);
      resetForm();
      setScheduledTime(dayjs().add(1, 'hour'));
    } catch (error: any) {
      console.error('Schedule error:', error);
      toast.error('Failed to schedule post. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Create New Post
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => dispatch(clearError())}>
            {error}
          </Alert>
        )}

        {generationJob && (
          <GenerationJobBanner job={generationJob} />
        )}

        <Grid container spacing={3}>
          {/* Left Column - Input and Controls */}
          <Grid item xs={12} md={6}>
            {/* Trending Topics */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Trending Topics</Typography>
                <Button
                  variant="outlined"
                  startIcon={<TrendingUp />}
                  onClick={handleFetchTrends}
                  disabled={loading}
                  size="small"
                >
                  Fetch Trends
                </Button>
              </Box>
              
              {trendingTopics.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {trendingTopics.map((trend, index) => (
                    <Card 
                      key={index} 
                      variant="outlined" 
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                        border: selectedTopic?.title === trend.title ? 2 : 1,
                        borderColor: selectedTopic?.title === trend.title ? 'primary.main' : 'divider'
                      }}
                      onClick={() => handleSelectTopic(trend)}
                    >
                      <CardContent sx={{ py: 1.5 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          {trend.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {trend.body.substring(0, 100)}...
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Click "Fetch Trends" to get trending topics
                </Typography>
              )}
            </Paper>

            {/* Topic Input */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Post Topic
              </Typography>
              <TextField
                fullWidth
                label="Enter your topic"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g., AI in Healthcare, Remote Work Tips, etc."
                sx={{ mb: 2 }}
              />
              {activeBrand && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Brand voice active: <strong>{activeBrand.brand_name}</strong>
                  {activeBrand.tone_of_voice ? ` — ${activeBrand.tone_of_voice}` : ''}
                </Alert>
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={generateThreeVariants}
                    onChange={(e) => setGenerateThreeVariants(e.target.checked)}
                  />
                }
                label="Generate 3 variants (Professional, Casual, Bold)"
                sx={{ mb: 1, display: 'block' }}
              />
              {generateThreeVariants && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Runs in the background via Celery — you can edit the topic and queue another while one is in progress.
                </Typography>
              )}
              <Button
                variant="contained"
                startIcon={queueingPost ? <CircularProgress size={18} color="inherit" /> : <AutoAwesome />}
                onClick={handleGenerateContent}
                disabled={!topic.trim() || queueingPost}
                fullWidth
              >
                {queueingPost
                  ? 'Queuing…'
                  : isGenerating
                    ? 'Queue another generation'
                    : 'Generate for LinkedIn, X & Instagram'}
              </Button>
            </Paper>

            {/* Image Controls */}
            {generatedContent && (
              <Paper sx={{ p: 3 }}>
                <ImageSearchPanel
                  topic={topic}
                  postContent={activePostContent}
                  defaultQuery={generatedContent.image_search_terms}
                  orientation={imageOrientation}
                  onOrientationChange={setImageOrientation}
                  imageQuery={imageQuery}
                  onImageQueryChange={(value) => dispatch(setImageQuery(value))}
                  suggestions={imageSuggestions}
                  candidates={imageCandidates}
                  selectedPreviewUrl={selectedPreviewUrl}
                  loadingSuggestions={imageSuggestionsLoading}
                  loadingSearch={imageSearchLoading}
                  selectingImage={imageSelecting}
                  onSuggestTitles={handleSuggestImageTitles}
                  onSearchImages={handleSearchImages}
                  onSelectSuggestion={handleSelectSuggestion}
                  onSelectImage={handleSelectImage}
                />
                <Button
                  variant="outlined"
                  onClick={() => handleRegeneratePart('hashtags')}
                  disabled={partRegenerating}
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  Regenerate hashtags (active tab)
                </Button>
              </Paper>
            )}
          </Grid>

          {/* Right Column - Generated Content */}
          <Grid item xs={12} md={6}>
            {generatedContent ? (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Platform Drafts
                </Typography>

                {savedDraftId && (
                  <Chip label={`Draft ID: ${savedDraftId.slice(0, 8)}…`} size="small" sx={{ mb: 2 }} />
                )}

                {isGenerating && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    New drafts will appear when the current job finishes. You can queue another topic anytime.
                  </Alert>
                )}

                {draftHasRefusal(platformDrafts) && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    The AI returned a refusal instead of a post — fix the topic spelling (e.g. &quot;Photography&quot;) and generate again.
                  </Alert>
                )}

                {hasOverLimitDraft && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    One or more platforms exceed the character limit. Edit before publishing to X or Instagram.
                  </Alert>
                )}

                <PostVariantPicker
                  variants={postVariants}
                  selectedIndex={selectedVariantIndex}
                  onSelect={handleSelectVariant}
                />
                
                <PlatformContentTabs
                  platformDrafts={platformDrafts}
                  activePlatform={activePlatform}
                  onActivePlatformChange={(platform) => dispatch(setActivePlatform(platform))}
                  onContentChange={(platform, content) =>
                    dispatch(updatePlatformDraftContent({ platform, content }))
                  }
                />

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                  {activePlatform === 'twitter' && (
                    <Button
                      variant="contained"
                      size="small"
                      color="secondary"
                      onClick={handleShortenForX}
                      disabled={shorteningForX || !(platformDrafts.linkedin?.content || generatedContent?.content)}
                      startIcon={shorteningForX ? <CircularProgress size={14} /> : <AutoAwesome />}
                    >
                      Shorten from LinkedIn
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleRegeneratePart('hook')}
                    disabled={partRegenerating}
                    startIcon={partRegenerating ? <CircularProgress size={14} /> : <AutoAwesome />}
                  >
                    Regenerate hook
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleRegeneratePart('hashtags')}
                    disabled={partRegenerating}
                  >
                    Regenerate hashtags
                  </Button>
                </Box>

                {/* Generated Image */}
                {selectedImage ? (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Selected Image
                    </Typography>
                    <Card>
                      <Box sx={{ position: 'relative', minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100' }}>
                        <img
                          src={getImageUrl(selectedImage.path)}
                          alt="Generated content"
                          style={{
                            width: '100%',
                            height: '200px',
                            objectFit: 'cover'
                          }}
                          onLoad={() => {
                            console.log('Image loaded successfully from:', getImageUrl(selectedImage.path));
                          }}
                          onError={(e) => {
                            console.error('Image failed to load from backend, trying Unsplash URL:', selectedImage.url);
                            console.error('Backend static files not configured. Image should be at:', getImageUrl(selectedImage.path));
                            // Show helpful error message
                            e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZmY0NDQ0Ii8+CiAgPHRleHQgeD0iNDAwIiB5PSIxODAiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIyMCIgZmlsbD0iI2ZmZiIgdGV4dC1hbmNob3I9Im1pZGRsZSI+QmFja2VuZCBTdGF0aWMgRmlsZXMgTm90IFNlcnZlZDwvdGV4dD4KICA8dGV4dCB4PSI0MDAiIHk9IjIwNSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0IiBmaWxsPSIjZmZmIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5JbWFnZSBwYXRoOiAke3NlbGVjdGVkSW1hZ2UucGF0aH08L3RleHQ+CiAgPHRleHQgeD0iNDAwIiB5PSIyMjUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMiIgZmlsbD0iI2ZmZiIgdGV4dC1hbmNob3I9Im1pZGRsZSI+Q29uZmlndXJlIEZhc3RBUEkgc3RhdGljIGZpbGUgc2VydmluZzwvdGV4dD4KPC9zdmc+';
                          }}
                        />
                      </Box>
                      <CardContent>
                        <Typography variant="caption" color="text.secondary">
                          {selectedImage.credit}
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ fontSize: '0.7rem', opacity: 0.6, mt: 1 }}>
                          <strong>Image loaded from backend:</strong> {selectedImage.path}
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ fontSize: '0.6rem', opacity: 0.4, mt: 0.5 }}>
                          Source: {selectedImage.url}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>
                ) : generatedContent && (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                      Suggest titles, search images, then click one to select it for your post.
                    </Typography>
                  </Alert>
                )}

                {imageQuery && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Active image search title
                    </Typography>
                    <Chip label={imageQuery} variant="outlined" size="small" />
                  </Box>
                )}

                <Divider sx={{ my: 3 }} />

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    startIcon={<Save />}
                    size="small"
                    onClick={handleSaveDraft}
                    disabled={actionLoading}
                  >
                    {actionLoading ? <CircularProgress size={16} /> : 'Save Draft'}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Publish />}
                    size="small"
                    onClick={openPublishDialog}
                    disabled={actionLoading}
                  >
                    Publish Now
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Schedule />}
                    size="small"
                    onClick={() => setScheduleDialogOpen(true)}
                    disabled={actionLoading}
                  >
                    Schedule
                  </Button>
                </Box>
              </Paper>
            ) : (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <AutoAwesome sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  AI Content Preview
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Enter a topic and click "Generate AI Content" to see your post preview here
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>

        {/* Publish Confirmation Dialog */}
        <Dialog
          open={publishConfirmOpen}
          onClose={() => setPublishConfirmOpen(false)}
          aria-labelledby="publish-dialog-title"
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle id="publish-dialog-title">
            Publish to platforms
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Choose which platforms to publish now. Each platform uses its tab content.
            </DialogContentText>
            <MultiPublishPanel
              platformDrafts={platformDrafts}
              connectedDraftKeys={connectedDraftKeys}
              selectedPlatforms={selectedPublishPlatforms}
              onSelectionChange={setSelectedPublishPlatforms}
            />
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setPublishConfirmOpen(false)}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button 
              onClick={handlePublishNow}
              variant="contained"
              disabled={
                actionLoading ||
                selectedPublishPlatforms.length === 0 ||
                getOverLimitSelections(platformDrafts, selectedPublishPlatforms).length > 0
              }
              startIcon={actionLoading ? <CircularProgress size={16} /> : <Publish />}
            >
              {actionLoading ? 'Publishing...' : `Publish (${selectedPublishPlatforms.length})`}
            </Button>
          </DialogActions>
        </Dialog>

        <PublishResultsDialog
          open={publishResultsOpen}
          results={publishResults}
          onClose={() => setPublishResultsOpen(false)}
        />

        {/* Schedule Dialog */}
        <Dialog
          open={scheduleDialogOpen}
          onClose={() => setScheduleDialogOpen(false)}
          aria-labelledby="schedule-dialog-title"
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle id="schedule-dialog-title">
            Schedule Post
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 3 }}>
              Choose when you want this post to be published.
            </DialogContentText>
            <DateTimePicker
              label="Scheduled Date & Time"
              value={scheduledTime}
              onChange={(newValue) => setScheduledTime(newValue)}
              minDateTime={dayjs()}
              sx={{ width: '100%' }}
            />
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setScheduleDialogOpen(false)}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSchedulePost}
              variant="contained"
              disabled={actionLoading}
              startIcon={actionLoading ? <CircularProgress size={16} /> : <Schedule />}
            >
              {actionLoading ? 'Scheduling...' : 'Schedule Post'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </LocalizationProvider>
  );
};

export default CreatePostPage; 